import React, { useState } from 'react';
import { 
  Building2, 
  Package, 
  Box,
  Plane, 
  Sparkles, 
  ShieldCheck, 
  Camera, 
  Boxes, 
  Clock, 
  HelpCircle, 
  ArrowRight, 
  CheckCircle2, 
  ShoppingBag, 
  Calculator, 
  Headphones, 
  Zap, 
  ExternalLink,
  ChevronDown,
  Globe,
  DollarSign,
  Heart,
  Star,
  Users,
  Truck,
  User,
  Lock
} from 'lucide-react';
import { ShippingCalculator } from './ShippingCalculator';

interface LandingPageProps {
  onEnterSuite: () => void;
  onOpenAuth: () => void;
  onOpenStoresGuide?: () => void;
  suiteNumber?: string;
  userName?: string;
  isLoggedIn?: boolean;
}

export const LandingPage: React.FC<LandingPageProps> = ({
  onEnterSuite,
  onOpenAuth,
  onOpenStoresGuide,
  suiteNumber = 'DJ-0000',
  userName = '',
  isLoggedIn = false
}) => {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const steps = [
    {
      num: '01',
      title: 'Cadastre-se e Obtenha seu Endereço no Japão',
      desc: 'Ao criar sua conta gratuita, você recebe instantaneamente seu endereço físico exclusivo no nosso depósito, com o número único da sua Suíte (ex: DJ-0000).',
      icon: Building2,
      color: 'bg-blue-50 text-blue-600 border-blue-200'
    },
    {
      num: '02',
      title: 'Compre em Qualquer Loja Japonesa',
      desc: 'Faça compras na Amazon Japan, Rakuten, Mercari JP, Surugaya, Yahoo Auctions, Bandai e insira seu endereço recebido de sua suíte no checkout.',
      icon: ShoppingBag,
      color: 'bg-rose-50 text-rose-600 border-rose-200'
    },
    {
      num: '03',
      title: 'Recebimento & Fotos em Alta Definição',
      desc: 'Assim que seu pacote chega no depósito, nossa equipe pesa e tira fotos HD dos produtos. Você é notificado no celular e painel na hora que o produto é cadastrado em sua suíte!',
      icon: Camera,
      color: 'bg-amber-50 text-amber-600 border-amber-200'
    },
    {
      num: '04',
      title: 'Consolidação Inteligente de Pacotes',
      desc: 'Junte compras de várias lojas em uma só caixa reforçada. Descartamos embalagens externas desnecessárias para você economizar até 80% no frete.',
      icon: Boxes,
      color: 'bg-indigo-50 text-indigo-600 border-indigo-200'
    },
    {
      num: '05',
      title: 'Envio Rápido e Seguro para o Brasil',
      desc: 'Escolha a modalidade de frete ideal para seu bolso e para a sua necessidade com código de rastreamento de ponta a ponta.',
      icon: Plane,
      color: 'bg-emerald-50 text-emerald-600 border-emerald-200'
    }
  ];

  const benefits = [
    {
      icon: Boxes,
      title: 'Consolidação de Pacotes sem Taxas Extra',
      desc: 'Combine múltiplos itens comprados em datas e lojas diferentes em uma única caixa para pagar apenas uma taxa de frete internacional.'
    },
    {
      icon: Clock,
      title: '60 Dias de Armazenamento Grátis',
      desc: 'Guarde suas compras com segurança em nosso depósito climatizado em Saitama por até 2 meses sem pagar nada de diária.'
    },
    {
      icon: Camera,
      title: 'Conferência e Fotos HD Incluídas',
      desc: 'Receba fotos nítidas dos produtos assim que chegarem para garantir que comprou exatamente o item correto antes do envio.'
    },
    {
      icon: Headphones,
      title: 'Atendimento VIP em Português',
      desc: 'Esqueça as barreiras do idioma! Nossa equipe de brasileiros e japoneses em Saitama atende você diretamente via WhatsApp.'
    },
    {
      icon: ShieldCheck,
      title: 'Declaração Personalizada & Documentação de Exportação',
      desc: 'O cliente mesmo faz a sua declaração personalizada e nossa equipe faz toda a documentação extra de exportação necessária para sua caixa chegar no Brasil evitando as altas taxas de importação.'
    },
    {
      icon: DollarSign,
      title: 'Pagamento Facilitado via PIX ou Cartão',
      desc: 'Pague fretes e serviços adicionais em Reais (R$) com conversão justa e sem surpresas com IOF abusivo de cartão internacional.'
    }
  ];

  const popularStores = [
    { name: 'Mercari JP', desc: 'Bazar de colecionáveis, cards Pokémon e seminovos', tag: 'Raridades e Usados', url: 'https://jp.mercari.com' },
    { name: 'Yahoo Auctions JP', desc: 'Leilões de itens raros, relógios e peças vintage', tag: 'Leilões do Japão', url: 'https://auctions.yahoo.co.jp' },
    { name: 'Surugaya', desc: 'Games retro, animes, figures e mangás', tag: 'Para Otakus e Gamers', url: 'https://www.suruga-ya.jp' },
    { name: 'ZOZOTOWN', desc: 'A maior loja de moda e streetwear do Japão', tag: 'Moda Japonesa', url: 'https://zozo.jp' },
    { name: 'AmiAmi', desc: 'Action figures em escala e pré-vendas oficiais', tag: 'Figures & Statues', url: 'https://www.amiami.jp' },
    { name: 'Rakuma (Rakuten)', desc: 'Marketplace de grifes, vestidos e artigos de luxo', tag: 'Feira C2C Rakuten', url: 'https://rakuma.rakuten.co.jp' },
    { name: 'Animate Online', desc: 'Produtos oficiais de animes, voice actors e CDs', tag: 'Loja Oficial Anime', url: 'https://www.animate-onlineshop.jp' },
    { name: 'Premium Bandai', desc: 'Action figures de Gundam, Kamen Rider e Dragon Ball', tag: 'Colecionáveis Bandai', url: 'https://p-bandai.jp' },
    { name: 'Pokémon Center', desc: 'Pelúcias, cartas TCG e produtos exclusivos do Japão', tag: 'Pokémon Oficial', url: 'https://www.pokemoncenter-online.com' },
    { name: 'Rakuten Ichiba', desc: 'Grande shopping virtual com milhares de lojistas', tag: 'Variedade Total', url: 'https://www.rakuten.co.jp' },
    { name: 'Amazon Japan', desc: 'Eletrônicos, livros e utilidades domésticas', tag: 'Entrega Rápida', url: 'https://amazon.co.jp' },
    { name: 'Bic Camera', desc: 'Câmeras, fotografia, relógios Seiko/Casio e games', tag: 'Eletrônicos & Câmeras', url: 'https://www.biccamera.com' }
  ];

  const faqs = [
    {
      q: 'O que é um serviço de redirecionamento de encomendas do Japão?',
      a: 'O redirecionamento do Japão fornece a você um endereço físico em nosso depósito em Saitama, Japão. Ao comprar em lojas japonesas que não entregam diretamente no Brasil (como Mercari JP, Rakuten, Yahoo Auctions e Surugaya), você envia os pacotes para sua Suíte. Nós recebemos, tiramos fotos HD, consolidamos e enviamos com o melhor frete internacional.'
    },
    {
      q: 'Como comprar no Mercari Japan estando no Brasil?',
      a: 'O Mercari JP é o maior marketplace de produtos usados, seminovos, figures raras e colecionáveis do Japão. Como o site exige conta japonesa e número de celular local, oferecemos o serviço de Compra Assistida / Personal Shopper: nossa equipe em Saitama faz a compra pelo vendedor japonês, recebe o produto em sua Suíte e prepara para envio ao Brasil.'
    },
    {
      q: 'Como participar dos leilões no Yahoo Auctions Japan (Yahoo Leilão do Japão)?',
      a: 'No Yahoo! Auctions Japan você encontra itens antigos, colecionáveis de animes, eletrônicos e relógios com ótimos preços. Através do nosso serviço de Compra Assistida de Leilão do Japão, você nos envia o link do anúncio e o valor máximo de lance desejado. Nossa equipe dá os lances em tempo real no Yahoo Japan e garante sua vitória.'
    },
    {
      q: 'Quanto custa para abrir uma Suíte no Japão?',
      a: 'A abertura da sua Suíte é 100% GRATUITA! Não cobramos taxa de adesão, mensalidades ou custos fixos de manutenção. Você tem 60 dias de armazenamento grátis e só paga pelo serviço de envio quando decidir mandar a caixa para o Brasil.'
    },
    {
      q: 'Como funciona a economia com a Consolidação de Pacotes do Japão?',
      a: 'O frete internacional do Japão possui um custo fixo inicial. Se você enviar 5 compras separadas do Mercari ou Amazon JP, pagará 5 taxas de frete. Ao consolidar tudo em 1 caixa reforçada, descarte de embalagens secundárias e remoção de espaços vazios, você economiza até 80% no frete.'
    },
    {
      q: 'Quais modalidades de frete do Japão para o Brasil posso escolher?',
      a: 'Trabalhamos com os métodos oficiais do Japan Post (EMS Express, Airmail Small Packet, SAL Econômico e Surface Marítimo por Navio) e fretes por transportadoras privadas de alta velocidade (Courier FedEx, UPS, DHL) com rastreamento completo até a entrega no seu endereço no Brasil.'
    },
    {
      q: 'Posso declarar o valor dos produtos na alfândega?',
      a: 'Sim! No painel exclusivo da sua Suíte no site Do Japão Até a Sua Casa, você tem total autonomia para preencher a declaração alfandegária e a discriminação dos itens para envio, cumprindo as exigências dos Correios e da Receita Federal do Brasil.'
    },
    {
      q: 'Posso pedir suporte se não souber comprar em sites japoneses?',
      a: 'Sim! Nosso suporte via WhatsApp orienta você sobre como criar conta em lojas japonesas, preencher o endereço da suíte corretamente e acompanhar seus pedidos com auxílio do Personal Shopper.'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 font-sans antialiased">
      
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#1D3557] via-[#1a3152] to-[#12223a] text-white pt-12 pb-20 sm:pt-16 sm:pb-28">
        
        {/* Background decorative grid and light glow */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:32px_32px]" />
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[600px] h-[350px] bg-sky-500/15 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Left Column: Hero Text */}
            <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
              
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 border border-white/15 text-xs font-bold text-sky-200 backdrop-blur-md">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span>Depósito • Japão 🇯🇵 ➔ 🇧🇷</span>
              </div>

              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-tight sm:leading-none">
                Seu endereço exclusivo no <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 via-rose-300 to-amber-300">Japão</span>.
                <br className="hidden sm:inline" /> Compre sem Fronteiras.
              </h1>

              <p className="text-base sm:text-lg text-slate-300 max-w-2xl mx-auto lg:mx-0 font-normal leading-relaxed">
                Adquira colecionáveis, animes, eletrônicos, cosméticos e produtos exclusivos nas melhores lojas do Japão. Receba em nosso depósito, consolide suas caixas e economize até <strong className="text-white font-bold underline decoration-rose-500 underline-offset-4">80% no frete internacional</strong>.
              </p>

              {/* Action Buttons */}
              <div className="pt-2 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 sm:gap-4">
                {isLoggedIn ? (
                  <>
                    <button
                      onClick={onEnterSuite}
                      className="w-full sm:w-auto px-8 py-4 bg-[#E63946] hover:bg-[#d62839] active:scale-95 text-white font-black text-sm sm:text-base rounded-2xl shadow-xl shadow-red-500/25 transition-all flex items-center justify-center gap-2.5 cursor-pointer group"
                    >
                      <Building2 className="w-5 h-5 text-white" />
                      <span>Acessar Minha Suíte #{suiteNumber}</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>

                    <button
                      onClick={onOpenAuth}
                      className="w-full sm:w-auto px-6 py-4 bg-white/10 hover:bg-white/20 text-white font-bold text-sm rounded-2xl border border-white/20 backdrop-blur-md transition-all flex items-center justify-center gap-2 cursor-pointer"
                    >
                      <User className="w-4 h-4 text-sky-300" />
                      <span>Alternar Usuário</span>
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={onOpenAuth}
                      className="w-full sm:w-auto px-8 py-4 bg-[#E63946] hover:bg-[#d62839] active:scale-95 text-white font-black text-sm sm:text-base rounded-2xl shadow-xl shadow-red-500/25 transition-all flex items-center justify-center gap-2.5 cursor-pointer group"
                    >
                      <Sparkles className="w-5 h-5 text-amber-300 animate-pulse" />
                      <span>Criar Minha Suíte Grátis 🇯🇵</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>

                    <button
                      onClick={onOpenAuth}
                      className="w-full sm:w-auto px-6 py-4 bg-white/10 hover:bg-white/20 text-white font-bold text-sm rounded-2xl border border-white/20 backdrop-blur-md transition-all flex items-center justify-center gap-2 cursor-pointer"
                    >
                      <Building2 className="w-4 h-4 text-slate-200" />
                      <span>Já sou Cliente / Entrar</span>
                    </button>
                  </>
                )}
              </div>

              {/* Mini Features Pills */}
              <div className="pt-4 border-t border-white/10 grid grid-cols-3 gap-2 sm:gap-4 text-left">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  <span className="text-xs text-slate-300 font-medium">60 Dias Grátis</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  <span className="text-xs text-slate-300 font-medium">Fotos HD Grátis</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  <span className="text-xs text-slate-300 font-medium">Suporte em PT-BR</span>
                </div>
              </div>

            </div>

            {/* Right Column: Visual Suite Preview Card */}
            <div className="lg:col-span-5">
              <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-6 sm:p-7 shadow-2xl space-y-5 text-slate-900 relative">
                
                <div className="flex items-center justify-between pb-4 border-b border-white/10 text-white">
                  <div>
                    <span className="text-[10px] font-extrabold text-sky-300 uppercase tracking-widest block">Seu Endereço de Entrega</span>
                    <h3 className="text-lg font-black font-mono mt-0.5">
                      Saitama Suite: {isLoggedIn ? suiteNumber : 'DJ-0000 (Exemplo)'}
                    </h3>
                  </div>
                  <span className={`px-3 py-1 text-xs font-bold rounded-full border flex items-center gap-1.5 ${
                    isLoggedIn 
                      ? 'bg-emerald-500/20 text-emerald-300 border-emerald-400/30' 
                      : 'bg-amber-500/20 text-amber-300 border-amber-400/30'
                  }`}>
                    <span className={`w-2 h-2 rounded-full ${isLoggedIn ? 'bg-emerald-400 animate-pulse' : 'bg-amber-400'}`} />
                    {isLoggedIn ? 'Ativo no Depósito' : 'Requer Cadastro'}
                  </span>
                </div>

                {/* Address Box Preview (Blurred for non-logged-in visitors) */}
                <div className="relative rounded-2xl overflow-hidden border border-slate-700/80">
                  <div className={`bg-slate-900/90 text-slate-200 p-4 text-xs font-mono space-y-1.5 transition-all ${
                    !isLoggedIn ? 'blur-md select-none opacity-30 pointer-events-none' : ''
                  }`}>
                    <p className="text-slate-400 font-sans font-bold text-[11px] uppercase tracking-wider mb-1">📋 Copie para os sites japoneses:</p>
                    <p><span className="text-slate-400">Nome:</span> Do Japao <strong className="text-amber-400 font-black">{isLoggedIn ? suiteNumber : 'DJ-0000'}</strong></p>
                    <p><span className="text-slate-400">CEP (Postal):</span> 〒350-1151</p>
                    <p><span className="text-slate-400">Província:</span> Saitama-ken (埼玉県)</p>
                    <p><span className="text-slate-400">Cidade:</span> Kawagoe-shi (川越市)</p>
                    <p><span className="text-slate-400">Endereço:</span> Imafuku 766-9 <strong className="text-amber-400 font-black">{isLoggedIn ? suiteNumber : 'DJ-0000'}</strong></p>
                    <p><span className="text-slate-400">Campo Extra (Opcional):</span> {isLoggedIn ? (userName || 'Camila Silva') : 'Camila Silva'} {isLoggedIn ? suiteNumber : 'DJ-0000'}</p>
                    <p><span className="text-slate-400">Telefone:</span> 080-4859-1502</p>
                  </div>

                  {!isLoggedIn && (
                    <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-xs flex flex-col items-center justify-center p-4 text-center z-10 space-y-3">
                      <div className="p-2.5 bg-amber-500/20 text-amber-300 rounded-full border border-amber-500/40 shadow-inner">
                        <Lock className="w-5 h-5 text-amber-400" />
                      </div>
                      <div>
                        <h4 className="text-white font-extrabold text-xs sm:text-sm">Endereço de Suíte Privado</h4>
                        <p className="text-slate-300 text-[11px] max-w-xs mt-1 leading-snug">
                          Cadastre-se ou faça login para liberar o seu endereço físico no Japão.
                        </p>
                      </div>
                      <button
                        onClick={onOpenAuth}
                        className="px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-slate-950 font-black text-xs rounded-xl shadow-md transition-all flex items-center gap-1.5 cursor-pointer active:scale-95"
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                        <span>Ver Meu Endereço no Japão</span>
                      </button>
                    </div>
                  )}
                </div>

                {/* Dashboard Stats Teaser (Shown only when client is logged in) */}
                {isLoggedIn && (
                  <div className="grid grid-cols-2 gap-3 pt-1">
                    <div className="bg-white/90 p-3.5 rounded-2xl border border-white/40 shadow-xs">
                      <div className="flex items-center justify-between text-slate-500 text-xs font-bold">
                        <span>Pacotes no Depósito</span>
                        <Package className="w-4 h-4 text-indigo-600" />
                      </div>
                      <div className="text-2xl font-black text-slate-900 mt-1">3 itens</div>
                      <span className="text-[10px] font-semibold text-emerald-600">Fotos prontas</span>
                    </div>

                    <div className="bg-white/90 p-3.5 rounded-2xl border border-white/40 shadow-xs">
                      <div className="flex items-center justify-between text-slate-500 text-xs font-bold">
                        <span>Economia Est.</span>
                        <Sparkles className="w-4 h-4 text-rose-500" />
                      </div>
                      <div className="text-2xl font-black text-emerald-600 mt-1">~72%</div>
                      <span className="text-[10px] font-semibold text-slate-500">via Consolidação</span>
                    </div>
                  </div>
                )}

                {isLoggedIn ? (
                  <button
                    onClick={onEnterSuite}
                    className="w-full py-3 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white font-extrabold text-xs sm:text-sm rounded-xl shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <span>Entrar no Painel da Suíte #{suiteNumber}</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={onOpenAuth}
                    className="w-full py-3.5 bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white font-extrabold text-xs sm:text-sm rounded-xl shadow-lg shadow-red-500/20 transition-all flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <Sparkles className="w-4 h-4 text-amber-300 animate-pulse" />
                    <span>Criar Conta Grátis & Ativar Minha Suíte</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                )}

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Trust Stats Bar */}
      <section className="bg-white border-b border-slate-200/80 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            
            <div className="space-y-1">
              <div className="text-2xl sm:text-3xl font-black text-[#1D3557]">+de 10 anos</div>
              <p className="text-xs text-slate-500 font-medium">de atividade</p>
            </div>

            <div className="space-y-1">
              <div className="text-2xl sm:text-3xl font-black text-[#E63946]">+50.000</div>
              <p className="text-xs text-slate-500 font-medium">Pacotes entregues no Brasil</p>
            </div>

            <div className="space-y-1">
              <div className="text-2xl sm:text-3xl font-black text-emerald-600">60 Dias</div>
              <p className="text-xs text-slate-500 font-medium">Armazenamento sem custos</p>
            </div>

            <div className="space-y-1">
              <div className="text-2xl sm:text-3xl font-black text-indigo-600">5 Opções</div>
              <p className="text-xs text-slate-500 font-medium">De fretes (Postais + Express)</p>
            </div>

          </div>
        </div>
      </section>

      {/* How it Works Section (Como Funciona Passo a Passo) */}
      <section id="como-funciona" className="py-16 sm:py-24 bg-slate-100/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          <div className="text-center max-w-3xl mx-auto space-y-3">
            <span className="px-3 py-1 bg-red-100 text-red-700 text-xs font-black rounded-full uppercase tracking-wider">
              Passo a Passo Simples
            </span>
            <h2 className="text-2xl sm:text-4xl font-black text-slate-900 tracking-tight">
              Como funciona o Redirecionamento do Japão?
            </h2>
            <p className="text-sm sm:text-base text-slate-600 font-normal">
              Comprar diretamente do Japão é fácil, seguro e transparente. Acompanhe toda a jornada da sua encomenda desde sua suíte até a sua porta no Brasil.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6 relative">
            {steps.map((step, idx) => {
              const IconComp = step.icon;
              return (
                <div 
                  key={step.num}
                  className="bg-white p-5 rounded-3xl border border-slate-200/80 shadow-xs flex flex-col justify-between hover:shadow-md transition-shadow relative"
                >
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <div className={`p-3 rounded-2xl border ${step.color}`}>
                        <IconComp className="w-6 h-6" />
                      </div>
                      <span className="text-2xl font-black font-mono text-slate-300">{step.num}</span>
                    </div>

                    <div>
                      <h3 className="text-sm font-extrabold text-slate-900 leading-snug">{step.title}</h3>
                      <p className="text-xs text-slate-500 mt-2 leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center pt-4">
            <button
              onClick={onEnterSuite}
              className="px-8 py-3.5 bg-[#1D3557] hover:bg-slate-800 text-white font-extrabold text-xs sm:text-sm rounded-xl shadow-md transition-all inline-flex items-center gap-2 cursor-pointer"
            >
              <span>Começar Agora - Obter Meu Endereço no Japão</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

        </div>
      </section>

      {/* Benefits Section */}
      <section id="beneficios" className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          <div className="text-center max-w-3xl mx-auto space-y-3">
            <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-black rounded-full uppercase tracking-wider">
              Por Que Escolher Nosso Serviço
            </span>
            <h2 className="text-2xl sm:text-4xl font-black text-slate-900 tracking-tight">
              Benefícios Exclusivos da Do Japão
            </h2>
            <p className="text-sm sm:text-base text-slate-600 font-normal">
              Oferecemos toda a infraestrutura física no Japão para você importar com total tranquilidade e suporte em português.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => {
              const BIcon = b.icon;
              return (
                <div 
                  key={i}
                  className="p-6 rounded-3xl bg-slate-50 border border-slate-200/80 hover:border-slate-300 transition-all space-y-3"
                >
                  <div className="w-12 h-12 bg-white rounded-2xl border border-slate-200 shadow-xs flex items-center justify-center text-[#1D3557]">
                    <BIcon className="w-6 h-6" />
                  </div>
                  <h3 className="text-base font-black text-slate-900">{b.title}</h3>
                  <p className="text-xs text-slate-600 leading-relaxed">{b.desc}</p>
                </div>
              );
            })}
          </div>

        </div>
      </section>

      {/* Services & Pricing Section (Serviços e Preços) */}
      <section id="servicos-e-precos" className="py-16 sm:py-24 bg-slate-100/80 border-y border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          <div className="text-center max-w-3xl mx-auto space-y-3">
            <span className="px-3 py-1 bg-emerald-100 text-emerald-800 text-xs font-black rounded-full uppercase tracking-wider inline-flex items-center gap-1.5">
              <DollarSign className="w-3.5 h-3.5" />
              <span>Transparência Total & Sem Pegadinhas</span>
            </span>
            <h2 className="text-2xl sm:text-4xl font-black text-slate-900 tracking-tight">
              Serviços e Preços
            </h2>
            <p className="text-sm sm:text-base text-slate-600 font-normal">
              Confira a tabela de valores dos nossos serviços de redirecionamento no Japão. Valores claros e sem custos ocultos.
            </p>
          </div>

          {/* Service Rate by Box Weight Highlights */}
          <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200/90 shadow-xs space-y-8">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Boxes className="w-5 h-5 text-[#E63946]" />
                <h3 className="text-lg sm:text-xl font-black text-slate-900">
                  Taxa de Serviço por Caixa Enviada (Redirecionamento)
                </h3>
              </div>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                A taxa de redirecionamento é calculada de acordo com o peso da caixa e é adicionada quando o cliente for pagar o frete:
              </p>
            </div>

            {/* 3 Main Pricing Cards for Box Weights */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              
              {/* Box up to 1kg */}
              <div className="bg-slate-50 rounded-2xl p-6 border-2 border-slate-200 hover:border-amber-400 transition-all space-y-4 relative overflow-hidden group">
                <div className="flex justify-between items-start">
                  <span className="px-3 py-1 bg-slate-200 text-slate-700 text-xs font-extrabold rounded-full">
                    Até 1 kg
                  </span>
                  <Box className="w-6 h-6 text-amber-500 group-hover:scale-110 transition-transform" />
                </div>
                <div>
                  <div className="text-xs text-slate-500 font-medium">Caixas Pequenas / Leves</div>
                  <div className="text-3xl font-black text-[#1D3557] font-mono mt-1">
                    $15,00 <span className="text-sm font-sans font-bold text-slate-500">USD</span>
                  </div>
                </div>
                <ul className="text-xs text-slate-600 space-y-2 pt-2 border-t border-slate-200/80">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>Ideal para roupas, pequenos eletrônicos e mangás</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>Adicionada ao checkout de pagamento do frete</span>
                  </li>
                </ul>
              </div>

              {/* Box up to 2kg */}
              <div className="bg-sky-50/70 rounded-2xl p-6 border-2 border-sky-300 hover:border-sky-500 transition-all space-y-4 relative overflow-hidden group shadow-xs">
                <div className="absolute top-0 right-0 bg-sky-600 text-white text-[10px] font-black px-3 py-0.5 rounded-bl-xl uppercase tracking-wider">
                  Mais Usado
                </div>
                <div className="flex justify-between items-start">
                  <span className="px-3 py-1 bg-sky-200 text-sky-900 text-xs font-extrabold rounded-full">
                    Até 2 kg
                  </span>
                  <Box className="w-6 h-6 text-sky-600 group-hover:scale-110 transition-transform" />
                </div>
                <div>
                  <div className="text-xs text-sky-800 font-medium">Caixas Médias</div>
                  <div className="text-3xl font-black text-[#1D3557] font-mono mt-1">
                    $20,00 <span className="text-sm font-sans font-bold text-slate-500">USD</span>
                  </div>
                </div>
                <ul className="text-xs text-slate-600 space-y-2 pt-2 border-t border-sky-200">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>Ideal para figures, cosméticos e múltiplos itens</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>Adicionada ao checkout de pagamento do frete</span>
                  </li>
                </ul>
              </div>

              {/* Box above 2kg */}
              <div className="bg-slate-50 rounded-2xl p-6 border-2 border-slate-200 hover:border-indigo-400 transition-all space-y-4 relative overflow-hidden group">
                <div className="flex justify-between items-start">
                  <span className="px-3 py-1 bg-slate-200 text-slate-700 text-xs font-extrabold rounded-full">
                    Acima de 2 kg
                  </span>
                  <Box className="w-6 h-6 text-indigo-600 group-hover:scale-110 transition-transform" />
                </div>
                <div>
                  <div className="text-xs text-slate-500 font-medium">Caixas Grandes / Pesadas</div>
                  <div className="text-3xl font-black text-[#1D3557] font-mono mt-1">
                    $30,00 <span className="text-sm font-sans font-bold text-slate-500">USD</span>
                  </div>
                </div>
                <ul className="text-xs text-slate-600 space-y-2 pt-2 border-t border-slate-200/80">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>Para consolidações volumosas de até 30kg</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span>Adicionada ao checkout de pagamento do frete</span>
                  </li>
                </ul>
              </div>

            </div>

            {/* Personal Shopper (Compra Assistida) Service Card */}
            <div className="pt-6 border-t border-slate-200/80">
              <div className="bg-gradient-to-br from-indigo-950 via-slate-900 to-[#1D3557] text-white rounded-3xl p-6 sm:p-8 space-y-6 shadow-md relative overflow-hidden">
                <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
                  <ShoppingBag className="w-48 h-48 text-white" />
                </div>

                <div className="flex flex-wrap justify-between items-start gap-4 relative z-10">
                  <div className="space-y-1.5 max-w-xl">
                    <span className="px-2.5 py-0.5 bg-indigo-500/30 text-indigo-200 text-[11px] font-black rounded-full uppercase tracking-wider border border-indigo-400/30 inline-flex items-center gap-1">
                      <Sparkles className="w-3 h-3 text-amber-300" />
                      <span>Compra Assistida</span>
                    </span>
                    <h3 className="text-xl sm:text-2xl font-black text-white flex items-center gap-2">
                      <ShoppingBag className="w-6 h-6 text-amber-400" />
                      <span>Personal Shopper no Japão</span>
                    </h3>
                    <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                      Nós compramos por você em sites japoneses (Mercari, Yahoo Auctions, Rakuten, Pokémon Center, Surugaya, Amazon JP, etc.) ou lojas físicas quando o site não aceita cadastros de fora do país ou cartões internacionais.
                    </p>
                  </div>

                  <div className="bg-white/10 backdrop-blur-md px-4 py-2.5 rounded-2xl border border-white/20 text-right font-mono self-start">
                    <span className="text-[10px] text-slate-300 uppercase font-bold block">Taxa Transparente</span>
                    <span className="text-amber-400 text-sm sm:text-base font-extrabold">Fixo ou 10%</span>
                  </div>
                </div>

                {/* Rates Comparison Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 relative z-10">
                  {/* Below 5000 JPY */}
                  <div className="p-4 bg-white/10 rounded-2xl border border-white/15 space-y-2 hover:bg-white/15 transition-all">
                    <div className="flex justify-between items-center text-xs font-bold text-slate-200 border-b border-white/10 pb-2">
                      <span>Produtos abaixo de ¥5.000 JPY</span>
                      <span className="px-2 py-0.5 bg-amber-400 text-slate-950 font-black rounded text-[10px] uppercase">
                        Taxa Fixa
                      </span>
                    </div>
                    <div className="flex justify-between items-baseline pt-1">
                      <span className="text-2xl sm:text-3xl font-black text-white font-mono">
                        ¥ 500 <span className="text-xs font-normal text-slate-300">JPY</span>
                      </span>
                      <span className="text-xs text-amber-300 font-semibold">(por produto)</span>
                    </div>
                    <p className="text-[11px] text-slate-300">
                      Taxa fixa para compras de menor valor (ex: cards, chaveiros, gashapon, mangás avulsos).
                    </p>
                  </div>

                  {/* Above 5000 JPY */}
                  <div className="p-4 bg-white/10 rounded-2xl border border-white/15 space-y-2 hover:bg-white/15 transition-all">
                    <div className="flex justify-between items-center text-xs font-bold text-slate-200 border-b border-white/10 pb-2">
                      <span>Produtos acima de ¥5.000 JPY</span>
                      <span className="px-2 py-0.5 bg-indigo-400 text-slate-950 font-black rounded text-[10px] uppercase">
                        Porcentagem
                      </span>
                    </div>
                    <div className="flex justify-between items-baseline pt-1">
                      <span className="text-2xl sm:text-3xl font-black text-white font-mono">
                        10% <span className="text-xs font-normal text-slate-300">do valor do produto</span>
                      </span>
                      <span className="text-xs text-indigo-200 font-semibold">(Ex: ¥10.000 = ¥1.000)</span>
                    </div>
                    <p className="text-[11px] text-slate-300">
                      Calculado proporcionalmente sobre o valor total do item comprado.
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-between text-xs text-slate-300 pt-2 border-t border-white/10 gap-3 relative z-10">
                  <span className="flex items-center gap-1.5 font-medium">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span>Negociação e lances rápidos no Mercari e Yahoo! Auctions</span>
                  </span>
                  <span className="flex items-center gap-1.5 font-medium">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span>Recebimento e registro automático na sua suíte do depósito</span>
                  </span>
                </div>
              </div>
            </div>

            {/* Included & Free Services Table / Grid */}
            <div className="pt-6 border-t border-slate-200/80">
              <h4 className="text-base font-black text-slate-900 mb-4 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-emerald-600" />
                <span>O que já está INCLUSO sem custos adicionais:</span>
              </h4>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="p-4 bg-emerald-50/60 rounded-2xl border border-emerald-200/80 space-y-1">
                  <div className="text-xs font-extrabold text-emerald-900 uppercase tracking-wider">Suíte no Japão</div>
                  <div className="text-lg font-black text-emerald-700">GRÁTIS ($0,00)</div>
                  <p className="text-[11px] text-slate-600">Abertura de conta e manutenção do seu endereço exclusivo em Saitama.</p>
                </div>

                <div className="p-4 bg-emerald-50/60 rounded-2xl border border-emerald-200/80 space-y-1">
                  <div className="text-xs font-extrabold text-emerald-900 uppercase tracking-wider">Armazenamento Inicial</div>
                  <div className="text-lg font-black text-emerald-700">60 Dias GRÁTIS</div>
                  <p className="text-[11px] text-slate-600">Guarde seus pacotes sem pagar nenhuma taxa ou mensalidade por até 2 meses.</p>
                </div>

                <div className="p-4 bg-emerald-50/60 rounded-2xl border border-emerald-200/80 space-y-1">
                  <div className="text-xs font-extrabold text-emerald-900 uppercase tracking-wider">Fotos HD ao Chegar</div>
                  <div className="text-lg font-black text-emerald-700">INCLUSO</div>
                  <p className="text-[11px] text-slate-600">Fotos de alta qualidade para você conferir e confirmar a qualidade do seu produto.</p>
                </div>

                <div className="p-4 bg-emerald-50/60 rounded-2xl border border-emerald-200/80 space-y-1">
                  <div className="text-xs font-extrabold text-emerald-900 uppercase tracking-wider">Consolidação</div>
                  <div className="text-lg font-black text-emerald-700">INCLUSO</div>
                  <p className="text-[11px] text-slate-600">Descarte de embalagens desnecessárias para economizar no frete.</p>
                </div>
              </div>
            </div>

            {/* Overdue Storage Fee Banner on Landing Page */}
            <div className="p-5 bg-gradient-to-r from-rose-900 via-slate-900 to-[#1D3557] text-white rounded-2xl border border-rose-700/60 space-y-3 shadow-md">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 pb-3">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 bg-rose-500/20 text-rose-300 rounded-xl border border-rose-400/30">
                    <Clock className="w-5 h-5 text-rose-300 animate-pulse" />
                  </div>
                  <div>
                    <h4 className="text-sm font-black text-white uppercase tracking-wider">
                      Taxa de Armazenamento Excedido (Após 60 Dias)
                    </h4>
                    <span className="text-[11px] text-slate-300">
                      Entenda como funciona a extensão de permanência no depósito
                    </span>
                  </div>
                </div>

                <span className="px-3 py-1 bg-amber-400 text-slate-950 font-black text-xs rounded-full shadow-2xs font-mono">
                  $30,00 USD / mês
                </span>
              </div>

              <p className="text-xs text-slate-200 leading-relaxed">
                Cada pacote possui <strong className="text-emerald-400">60 dias de armazenamento 100% gratuito</strong>. A partir do 61º dia que o pacote permanece no depósito, é cobrada uma taxa de mensalidade extra de <strong className="text-amber-300 font-mono font-bold">$30,00 USD / mês</strong>, acumulando progressivamente a cada mês adicional decorrido ($30 no 1º mês, $60 no 2º mês, $90 no 3º mês, etc.).
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs font-mono pt-1 text-slate-300">
                <div className="bg-white/10 p-2.5 rounded-xl border border-white/10 flex justify-between sm:flex-col sm:items-start">
                  <span className="text-[10px] text-slate-400 uppercase">Até 60 Dias</span>
                  <span className="font-extrabold text-emerald-400 text-sm">GRÁTIS ($0,00)</span>
                </div>
                <div className="bg-white/10 p-2.5 rounded-xl border border-white/10 flex justify-between sm:flex-col sm:items-start">
                  <span className="text-[10px] text-slate-400 uppercase">61º ao 90º Dia</span>
                  <span className="font-extrabold text-amber-300 text-sm">+$30,00 USD (1º mês)</span>
                </div>
                <div className="bg-white/10 p-2.5 rounded-xl border border-white/10 flex justify-between sm:flex-col sm:items-start">
                  <span className="text-[10px] text-slate-400 uppercase">A partir do 91º Dia</span>
                  <span className="font-extrabold text-rose-300 text-sm">+$60,00+ USD acumulativo</span>
                </div>
              </div>
            </div>

            {/* Note on Payment & Conversion */}
            <div className="p-4 bg-amber-50 rounded-2xl border border-amber-200 text-xs text-amber-900 flex items-start gap-3">
              <Zap className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <strong className="font-extrabold block text-slate-900 mb-0.5">Pagamento Transparente em Reais (BRL):</strong>
                <span>
                  Os valores do frete e da taxa de serviço são calculados em Dólares/Yens e convertidos em Reais (R$) com a taxa de transferência internacional de 7%, permitindo pagamento via PIX ou Cartão sem IOF abusivo.
                </span>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* Interactive Freight Calculator Preview Section */}
      <section id="calculadora" className="py-16 sm:py-24 bg-slate-900 text-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          
          <div className="text-center max-w-3xl mx-auto space-y-3">
            <span className="px-3 py-1 bg-amber-400/20 text-amber-300 border border-amber-400/30 text-xs font-black rounded-full uppercase tracking-wider">
              Simulador Transparente
            </span>
            <h2 className="text-2xl sm:text-4xl font-black tracking-tight">
              Calcule o Frete Internacional em Tempo Real
            </h2>
            <p className="text-sm sm:text-base text-slate-300 font-normal">
              Insira o peso estimado da sua encomenda e compare os preços via Japan Post (EMS, Small Packet, SAL, Navio) e Frete Courrier para o Brasil.
            </p>
          </div>

          <div className="bg-white text-slate-900 rounded-3xl p-4 sm:p-6 shadow-2xl">
            <ShippingCalculator />
          </div>

        </div>
      </section>

      {/* Popular Japanese Stores Guide */}
      <section className="py-16 sm:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <h2 className="text-2xl sm:text-3xl font-black text-slate-900">
              Onde Comprar no Japão?
            </h2>
            <p className="text-xs sm:text-sm text-slate-600">
              Explore as principais lojas online que mais fazem sucesso entre nossos clientes:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {popularStores.map((store, idx) => (
              <a
                key={idx}
                href={store.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white p-5 rounded-2xl border border-slate-200/80 hover:border-blue-400 shadow-xs hover:shadow-md transition-all group flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between">
                    <span className="px-2.5 py-0.5 bg-slate-100 text-slate-700 font-extrabold text-[10px] rounded-md uppercase tracking-wider">
                      {store.tag}
                    </span>
                    <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-blue-600 transition-colors" />
                  </div>
                  <h3 className="text-base font-black text-slate-900 mt-2">{store.name}</h3>
                  <p className="text-xs text-slate-500 mt-1">{store.desc}</p>
                </div>
                <div className="mt-4 text-xs font-bold text-blue-600 group-hover:underline flex items-center gap-1">
                  <span>Visitar Loja Japonesa</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </a>
            ))}
          </div>

          {/* Centered button to view more stores */}
          <div className="flex justify-center pt-4">
            <button
              onClick={() => {
                if (onOpenStoresGuide) {
                  onOpenStoresGuide();
                } else {
                  const event = new CustomEvent('openInfoTopic', { detail: 'sites-compras-japao' });
                  window.dispatchEvent(event);
                }
              }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#1D3557] hover:bg-[#15263f] active:scale-95 text-white font-extrabold text-xs sm:text-sm rounded-xl shadow-md transition-all cursor-pointer"
            >
              <Globe className="w-4 h-4 text-sky-300" />
              <span>Confira mais lojas</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

        </div>
      </section>

      {/* SEO Dedicated Keyword Knowledge Hub & Guides */}
      <section className="py-16 sm:py-24 bg-white border-y border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          <div className="text-center max-w-3xl mx-auto space-y-3">
            <span className="px-3 py-1 bg-red-100 text-red-800 text-xs font-black rounded-full uppercase tracking-wider inline-flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-red-600" />
              <span>Central de Conhecimento</span>
            </span>
            <h2 className="text-2xl sm:text-4xl font-black text-slate-900 tracking-tight">
              Tudo sobre Redirecionamento do Japão, Mercari & Leilão Yahoo Japan
            </h2>
            <p className="text-sm sm:text-base text-slate-600">
              Guia definitivo com as dúvidas mais frequentes para quem deseja comprar produtos japoneses e receber no Brasil.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            
            {/* SEO Pillar 1 */}
            <article className="p-6 bg-slate-50 rounded-3xl border border-slate-200 space-y-4 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-red-100 text-red-700 rounded-2xl flex items-center justify-center font-black">
                <Box className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-black text-slate-900">
                Redirecionamento do Japão para o Brasil
              </h3>
              <p className="text-xs text-slate-600 leading-relaxed">
                Com o nosso <strong className="text-slate-900">serviço de redirecionamento de encomendas do Japão</strong>, você obtém um <strong className="text-slate-900">endereço físico no Japão</strong> (sua <strong className="text-slate-900">suíte de compras no Japão</strong>) em nosso <strong className="text-slate-900">armando/armagém de compras no Japão</strong>. Funcionamos como a <strong className="text-slate-900">melhor empresa de redirecionamento do Japão</strong>, oferecendo <strong className="text-slate-900">consolidação de pacotes do Japão</strong>, <strong className="text-slate-900">personal shopper no Japão</strong> e serviço completo de <strong className="text-slate-900">compra assistida no Japão</strong> para <strong className="text-slate-900">comprar no Japão e enviar para o Brasil</strong> com facilidade.
              </p>
              <div className="pt-2 border-t border-slate-200/80 flex flex-wrap gap-1">
                <span className="text-[10px] bg-white border border-slate-200 px-2 py-0.5 rounded-md font-semibold text-slate-600">redirecionamento de compras japao brasil</span>
                <span className="text-[10px] bg-white border border-slate-200 px-2 py-0.5 rounded-md font-semibold text-slate-600">como ter um endereco no japao</span>
                <span className="text-[10px] bg-white border border-slate-200 px-2 py-0.5 rounded-md font-semibold text-slate-600">caixa postal no japao para brasileiros</span>
                <span className="text-[10px] bg-white border border-slate-200 px-2 py-0.5 rounded-md font-semibold text-slate-600">app de redirecionamento do japao</span>
              </div>
            </article>

            {/* SEO Pillar 2 */}
            <article className="p-6 bg-slate-50 rounded-3xl border border-slate-200 space-y-4 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-blue-100 text-blue-700 rounded-2xl flex items-center justify-center font-black">
                <ShoppingBag className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-black text-slate-900">
                Mercari Japan, Leilão Yahoo & Proxy Shopping
              </h3>
              <p className="text-xs text-slate-600 leading-relaxed">
                Aprenda <strong className="text-slate-900">como comprar no Mercari JP sem telefone japonês</strong> com o nosso <strong className="text-slate-900">proxy shopping Mercari Japan</strong>. Oferecemos suporte total para <strong className="text-slate-900">leilão Yahoo Japan Brasil (Yafuoku)</strong> com envio de lances em tempo real. Importe também de <strong className="text-slate-900">Rakuten Japão</strong>, <strong className="text-slate-900">Amazon Japan</strong>, <strong className="text-slate-900">Qoo10</strong>, <strong className="text-slate-900">Zozotown</strong>, <strong className="text-slate-900">Uniqlo</strong>, <strong className="text-slate-900">Muji</strong>, <strong className="text-slate-900">Surugaya</strong>, <strong className="text-slate-900">Pokémon Center Japan</strong>, <strong className="text-slate-900">2nd Street Japan</strong> e <strong className="text-slate-900">Bic Camera</strong>.
              </p>
              <div className="pt-2 border-t border-slate-200/80 flex flex-wrap gap-1">
                <span className="text-[10px] bg-white border border-slate-200 px-2 py-0.5 rounded-md font-semibold text-slate-600">comprar figure de anime no mercari</span>
                <span className="text-[10px] bg-white border border-slate-200 px-2 py-0.5 rounded-md font-semibold text-slate-600">leilao de artigos raros do japao</span>
                <span className="text-[10px] bg-white border border-slate-200 px-2 py-0.5 rounded-md font-semibold text-slate-600">otamato japan colecionaveis</span>
              </div>
            </article>

            {/* SEO Pillar 3 */}
            <article className="p-6 bg-slate-50 rounded-3xl border border-slate-200 space-y-4 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-emerald-100 text-emerald-700 rounded-2xl flex items-center justify-center font-black">
                <Truck className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-black text-slate-900">
                Fretes do Japão, Taxas & Impostos de Importação
              </h3>
              <p className="text-xs text-slate-600 leading-relaxed">
                Utilize nossa <strong className="text-slate-900">calculadora de frete do Japão</strong> e escolha entre <strong className="text-slate-900">EMS Japan Post rastreio</strong>, <strong className="text-slate-900">ePacket Japão</strong>, <strong className="text-slate-900">Small Packet com rastreio</strong>, <strong className="text-slate-900">Frete Marítimo Surface</strong>, <strong className="text-slate-900">DHL Express</strong> e <strong className="text-slate-900">FedEx Internacional</strong>. Entenda tudo sobre o <strong className="text-slate-900">imposto de importação do Japão</strong>, <strong className="text-slate-900">como declarar compras do Japão</strong>, <strong className="text-slate-900">despacho postal Correios</strong> e fiscalização da Receita Federal.
              </p>
              <div className="pt-2 border-t border-slate-200/80 flex flex-wrap gap-1">
                <span className="text-[10px] bg-white border border-slate-200 px-2 py-0.5 rounded-md font-semibold text-slate-600">quanto custa enviar uma caixa do japao</span>
                <span className="text-[10px] bg-white border border-slate-200 px-2 py-0.5 rounded-md font-semibold text-slate-600">conversor de moedas iene para real</span>
                <span className="text-[10px] bg-white border border-slate-200 px-2 py-0.5 rounded-md font-semibold text-slate-600">declaracao alfandegaria japan post</span>
              </div>
            </article>

          </div>

          {/* Categorized SEO Keywords Directory */}
          <div className="p-8 bg-slate-900 text-white rounded-3xl space-y-8 shadow-xl">
            <div className="border-b border-slate-800 pb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-black text-white flex items-center gap-2">
                  <Globe className="w-5 h-5 text-red-500" />
                  <span>Índice de Pesquisas & Categorias Populares do Japão</span>
                </h3>
                <p className="text-xs text-slate-400">
                  Dúvidas mais perguntadas a nossa equipe sobre compras e envios do Japão
                </p>
              </div>
              <span className="text-xs bg-red-600/30 border border-red-500/40 text-red-300 font-mono px-3 py-1 rounded-full w-fit">
                Guia definitivo Do Japão
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-xs">
              
              {/* Group 1 */}
              <div className="space-y-2">
                <h4 className="font-extrabold text-red-400 uppercase tracking-wider text-[11px] pb-1 border-b border-slate-800">
                  Redirecionamento & Suíte
                </h4>
                <ul className="space-y-1 text-slate-300 font-medium leading-relaxed">
                  <li>• redirecionamento de encomendas japao</li>
                  <li>• redirecionamento de compras japao brasil</li>
                  <li>• endereco fisico no japao</li>
                  <li>• empresa de redirecionamento japao</li>
                  <li>• suite de compras no japao</li>
                  <li>• redirecionador de encomendas japao</li>
                  <li>• consolidacao de pacotes do japao</li>
                  <li>• personal shopper no japao</li>
                  <li>• compra assistida japao</li>
                  <li>• como ter um endereco no japao</li>
                  <li>• melhor redirecionador do japao</li>
                  <li>• grupo de compras japao telegram</li>
                  <li>• app de redirecionamento do japao</li>
                </ul>
              </div>

              {/* Group 2 */}
              <div className="space-y-2">
                <h4 className="font-extrabold text-sky-400 uppercase tracking-wider text-[11px] pb-1 border-b border-slate-800">
                  Marketplaces & Proxies
                </h4>
                <ul className="space-y-1 text-slate-300 font-medium leading-relaxed">
                  <li>• comprar no mercari japao</li>
                  <li>• mercari japan redirecionamento</li>
                  <li>• mercari brasil como comprar</li>
                  <li>• como comprar no mercari sem telefone japones</li>
                  <li>• proxy shopping mercari japan</li>
                  <li>• leilao yahoo japan brasil</li>
                  <li>• como dar lance no yahoo auctions japao</li>
                  <li>• yafuoku brasil redirecionamento</li>
                  <li>• comprar no rakuten japao</li>
                  <li>• amazon japan entrega no brasil</li>
                  <li>• zozotown e uniqlo japao</li>
                  <li>• pokemon center japan compras</li>
                  <li>• 2nd street japan roupas usadas</li>
                </ul>
              </div>

              {/* Group 3 */}
              <div className="space-y-2">
                <h4 className="font-extrabold text-emerald-400 uppercase tracking-wider text-[11px] pb-1 border-b border-slate-800">
                  Frete, Rastreio & Cotação
                </h4>
                <ul className="space-y-1 text-slate-300 font-medium leading-relaxed">
                  <li>• frete do japao para o brasil</li>
                  <li>• epacket japao brasil prazo</li>
                  <li>• ems japan post rastreio</li>
                  <li>• small packet com rastreio japao</li>
                  <li>• frete maritimo do japao para o brasil</li>
                  <li>• dhl express e fedex envio japao</li>
                  <li>• calculadora de frete do japao</li>
                  <li>• frete economico japao brasil</li>
                  <li>• quanto custa enviar uma caixa do japao</li>
                  <li>• tabela de frete japan post</li>
                  <li>• rastreamento correios encomenda japao</li>
                  <li>• seguro de envio internacional japao</li>
                  <li>• conversor de moedas iene para real</li>
                </ul>
              </div>

              {/* Group 4 */}
              <div className="space-y-2">
                <h4 className="font-extrabold text-amber-400 uppercase tracking-wider text-[11px] pb-1 border-b border-slate-800">
                  Produtos & Categorias Niche
                </h4>
                <ul className="space-y-1 text-slate-300 font-medium leading-relaxed">
                  <li>• importar artigos de pesca do japao</li>
                  <li>• molinetes e carretilhas importados</li>
                  <li>• comprar cartas pokemon originais japao</li>
                  <li>• tcg pokemon japan importacao</li>
                  <li>• comprar action figures originais</li>
                  <li>• comprar relogios casio no japao</li>
                  <li>• cosmeticos e skincare japoneses</li>
                  <li>• videogames retro e switch mercari</li>
                  <li>• imposto de importacao produtos japao</li>
                  <li>• como declarar compras do japao</li>
                  <li>• tributacao e isencao receita federal</li>
                  <li>• produtos proibidos envio do japao</li>
                  <li>• desmembramento de pacotes importacao</li>
                </ul>
              </div>

            </div>
          </div>

        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 sm:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          
          <div className="text-center space-y-2">
            <span className="px-3 py-1 bg-purple-100 text-purple-800 text-xs font-black rounded-full uppercase tracking-wider">
              Tire Suas Dúvidas
            </span>
            <h2 className="text-2xl sm:text-4xl font-black text-slate-900 tracking-tight">
              Perguntas Frequentes
            </h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, index) => {
              const isOpen = openFaq === index;
              return (
                <div 
                  key={index}
                  className="border border-slate-200 rounded-2xl overflow-hidden transition-all"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full text-left p-5 bg-slate-50 hover:bg-slate-100/80 font-bold text-xs sm:text-sm text-slate-900 flex justify-between items-center gap-4 transition-colors cursor-pointer"
                  >
                    <span>{faq.q}</span>
                    <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isOpen ? 'rotate-180 text-blue-600' : ''}`} />
                  </button>

                  {isOpen && (
                    <div className="p-5 bg-white border-t border-slate-100 text-xs sm:text-sm text-slate-600 leading-relaxed">
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

        </div>
      </section>

      {/* Final CTA Banner */}
      <section className="bg-gradient-to-r from-[#1D3557] to-[#12223a] text-white py-16 text-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <h2 className="text-2xl sm:text-4xl font-black tracking-tight">
            Pronto para começar suas compras no Japão?
          </h2>
          <p className="text-sm sm:text-base text-slate-300 max-w-xl mx-auto">
            Abra sua conta grátis agora mesmo, receba o endereço da sua Suíte e aproveite todas as facilidades da Do Japão para receber seus produtos japoneses no conforto de sua casa.
          </p>
          <div className="pt-2 flex flex-col sm:flex-row justify-center gap-3">
            {isLoggedIn ? (
              <button
                onClick={onEnterSuite}
                className="px-8 py-4 bg-[#E63946] hover:bg-[#d62839] active:scale-95 text-white font-black text-sm rounded-2xl shadow-xl transition-all cursor-pointer flex items-center justify-center gap-2"
              >
                <Building2 className="w-5 h-5" />
                <span>Acessar Painel da Minha Suíte #{suiteNumber}</span>
              </button>
            ) : (
              <>
                <button
                  onClick={onOpenAuth}
                  className="px-8 py-4 bg-[#E63946] hover:bg-[#d62839] active:scale-95 text-white font-black text-sm rounded-2xl shadow-xl transition-all cursor-pointer flex items-center justify-center gap-2"
                >
                  <Sparkles className="w-5 h-5 text-amber-300" />
                  <span>Criar Conta Grátis & Obter Endereço no Japão</span>
                </button>
                <button
                  onClick={onOpenAuth}
                  className="px-6 py-4 bg-white/10 hover:bg-white/20 text-white font-bold text-sm rounded-2xl border border-white/20 transition-all cursor-pointer flex items-center justify-center gap-2"
                >
                  <Building2 className="w-4 h-4" />
                  <span>Fazer Login</span>
                </button>
              </>
            )}
          </div>
        </div>
      </section>

    </div>
  );
};

export default LandingPage;
